import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  api,
  ApiError,
  type CreateSalesTypeBody,
  type SalesType,
  type UpdateSalesTypeBody,
} from '../../lib/api.js';
import { useAuth } from '../../lib/auth-store.js';
import { cn } from '../../lib/cn.js';
import { SALES_TYPES_KEY, tintFor, useSalesTypes } from '../../lib/trade.js';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';
import { Button } from '../ui/button.js';
import { Feedback } from '../ui/feedback.js';
import { Input } from '../ui/input.js';
import { Pill } from '../ui/pill.js';

/**
 * Admin → Sales types. The company's job categories: label, colour, the
 * Spire values that map to each, and whether techs can hold it as a skill.
 * Types are deactivated, never deleted — historical orders keep matching
 * and techs keep the skill in their history.
 */
export function SalesTypesPanel() {
  const role = useAuth((s) => s.user?.role);
  const canEdit = role === 'admin';
  const queryClient = useQueryClient();
  const types = useSalesTypes();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<SalesType | null>(null);
  const [creating, setCreating] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SALES_TYPES_KEY });

  const toggleActive = useMutation<SalesType, ApiError, SalesType>({
    mutationFn: (t) => api.salesTypes.update(t.key, { active: !t.active }),
    onSuccess: invalidate,
  });
  const move = useMutation<unknown, ApiError, { key: string; dir: -1 | 1 }>({
    mutationFn: async ({ key, dir }) => {
      // Swap sortOrder with the neighbour in the visible list.
      const ordered = [...types].sort((a, b) => a.sortOrder - b.sortOrder);
      const i = ordered.findIndex((t) => t.key === key);
      const j = i + dir;
      const a = ordered[i];
      const b = ordered[j];
      if (!a || !b) return;
      await api.salesTypes.update(a.key, { sortOrder: b.sortOrder });
      await api.salesTypes.update(b.key, { sortOrder: a.sortOrder });
    },
    onSuccess: invalidate,
  });

  const ordered = [...types].sort((a, b) => a.sortOrder - b.sortOrder);
  const visible = showInactive ? ordered : ordered.filter((t) => t.active);

  return (
    <section
      className="rounded-xl border bg-surface p-4"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
      aria-labelledby="sales-types-title"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 id="sales-types-title" className="font-medium text-ink">
            Sales types
          </h2>
          <p className="text-xs text-ink-muted mt-0.5">
            Job categories and their colours, used on every job card and as tech skills. Each type
            lists the Spire <span className="font-mono">SALES TYP</span> values that map to it.
            {!canEdit && ' Only admins can change these.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          {canEdit && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              Add type
            </Button>
          )}
        </div>
      </div>

      <ul className="mt-3 divide-y" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        {visible.map((t, idx) => {
          const tint = tintFor(t.color);
          return (
            <li key={t.key} className={cn('flex items-center gap-3 py-2', !t.active && 'opacity-60')}>
              <span
                aria-hidden
                className="inline-block h-6 w-6 shrink-0 rounded-md border"
                style={{ backgroundColor: t.color, borderColor: tint.border }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                    style={{ backgroundColor: tint.bg, color: tint.fg }}
                  >
                    {t.label}
                  </span>
                  <span className="font-mono text-[11px] text-ink-subtle">{t.key}</span>
                  {!t.isSkill && <Pill variant="neutral">not a skill</Pill>}
                  {!t.active && <Pill variant="neutral">inactive</Pill>}
                </div>
                <div className="text-[11px] text-ink-muted truncate mt-0.5">
                  matches: {t.matchValues.length > 0 ? t.matchValues.join(', ') : '—'}
                </div>
              </div>
              {canEdit && (
                <div className="inline-flex items-center gap-2 shrink-0 text-xs">
                  <button
                    type="button"
                    disabled={idx === 0 || move.isPending}
                    onClick={() => move.mutate({ key: t.key, dir: -1 })}
                    className="text-ink-muted hover:text-ink disabled:opacity-30"
                    aria-label={`Move ${t.label} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    disabled={idx === visible.length - 1 || move.isPending}
                    onClick={() => move.mutate({ key: t.key, dir: 1 })}
                    className="text-ink-muted hover:text-ink disabled:opacity-30"
                    aria-label={`Move ${t.label} down`}
                  >
                    ↓
                  </button>
                  <button type="button" onClick={() => setEditing(t)} className="text-ink-accent hover:underline">
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={toggleActive.isPending}
                    onClick={() => toggleActive.mutate(t)}
                    className="text-ink-muted hover:text-ink"
                  >
                    {t.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {(toggleActive.isError || move.isError) && (
        <Feedback variant="error" size="sm" className="mt-2">
          {(toggleActive.error ?? move.error)?.message}
        </Feedback>
      )}

      {creating && (
        <SalesTypeDialog
          title="Add sales type"
          initial={{ label: '', color: '#185FA5', matchValues: '', isSkill: true }}
          onClose={() => setCreating(false)}
          submit={async (form) => {
            const body: CreateSalesTypeBody = {
              label: form.label,
              color: form.color,
              matchValues: splitValues(form.matchValues),
              isSkill: form.isSkill,
            };
            await api.salesTypes.create(body);
            await invalidate();
          }}
        />
      )}
      {editing && (
        <SalesTypeDialog
          title={`Edit ${editing.label}`}
          keyLabel={editing.key}
          initial={{
            label: editing.label,
            color: editing.color,
            matchValues: editing.matchValues.join(', '),
            isSkill: editing.isSkill,
          }}
          onClose={() => setEditing(null)}
          submit={async (form) => {
            const body: UpdateSalesTypeBody = {
              label: form.label,
              color: form.color,
              matchValues: splitValues(form.matchValues),
              isSkill: form.isSkill,
            };
            await api.salesTypes.update(editing.key, body);
            await invalidate();
          }}
        />
      )}
    </section>
  );
}

interface SalesTypeForm {
  label: string;
  color: string;
  /** Comma-separated in the form; split on save. */
  matchValues: string;
  isSkill: boolean;
}

function SalesTypeDialog({
  title,
  keyLabel,
  initial,
  submit,
  onClose,
}: {
  title: string;
  keyLabel?: string;
  initial: SalesTypeForm;
  submit: (form: SalesTypeForm) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<SalesTypeForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useDialogA11y<HTMLFormElement>(onClose);
  const tint = tintFor(form.color);
  const validColor = /^#[0-9a-fA-F]{6}$/.test(form.color);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.label.trim() || !validColor) return;
    setBusy(true);
    setError(null);
    try {
      await submit({ ...form, label: form.label.trim() });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
      <form
        ref={ref}
        onSubmit={(e) => void onSubmit(e)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-xl bg-surface p-4 space-y-3 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-ink">{title}</h3>
          {keyLabel && <span className="font-mono text-[11px] text-ink-subtle">{keyLabel}</span>}
        </div>

        <label className="block space-y-1 text-sm">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Label</span>
          <Input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            maxLength={40}
            data-autofocus
            required
          />
          {!keyLabel && (
            <span className="block text-[11px] text-ink-subtle">
              The key is made from the label and can't be changed later; the label can.
            </span>
          )}
        </label>

        <div className="space-y-1 text-sm">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Colour</span>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={validColor ? form.color : '#185FA5'}
              onChange={(e) => setForm({ ...form, color: e.target.value.toUpperCase() })}
              className="h-11 w-14 rounded-lg border bg-surface p-1"
              style={{ borderColor: 'var(--color-border-secondary)' }}
              aria-label="Pick a colour"
            />
            <Input
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value.trim() })}
              className="font-mono w-32"
              maxLength={7}
              aria-label="Hex colour"
            />
            <span
              className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ backgroundColor: tint.bg, color: tint.fg }}
            >
              {form.label.trim() || 'Preview'}
            </span>
          </div>
          {!validColor && <span className="block text-[11px] text-red-700">Use a hex colour like #185FA5.</span>}
        </div>

        <label className="block space-y-1 text-sm">
          <span className="text-xs uppercase tracking-wider text-ink-subtle">Spire values that mean this type</span>
          <Input
            value={form.matchValues}
            onChange={(e) => setForm({ ...form, matchValues: e.target.value })}
            placeholder="e.g. install, installation"
          />
          <span className="block text-[11px] text-ink-subtle">
            Comma-separated, case doesn't matter. An order's <span className="font-mono">SALES TYP</span>{' '}
            matches if it equals or starts with one of these.
          </span>
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isSkill}
            onChange={(e) => setForm({ ...form, isSkill: e.target.checked })}
          />
          Techs can hold this as a skill
        </label>

        {error && <Feedback variant="error" size="sm">{error}</Feedback>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={busy || !form.label.trim() || !validColor}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function splitValues(raw: string): string[] {
  return raw
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}
